import UserSettingsService from "src/services/userSettingsService";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { selectIsUserCredentialValid } from "src/store/auth";
import { isUnlimited } from "src/ui/utils/limit";

import { skipToken } from "@reduxjs/toolkit/query";
import AuthService from "@services/authService";
import TimeService from "@services/timeService/timeService";

import { FeatureType, WorkspaceMemberRole } from "../types";

import { GetWorkspaceFeaturesParams, GetWorkspacesParams } from "./rtkClient";
import { useGetWorkspaceFeaturesQuery, useGetWorkspacesQuery } from ".";

type GetWorkspacesQueryOptions = Omit<Parameters<typeof useGetWorkspacesQuery>[1], "selectFromResult">;

// Single source of params shared by every workspaces query. RTK Query keys the cache by params,
// so any divergence (e.g. one caller passing `includeTerminated`, another not) splits the cache
// and produces an extra network request. Read these defaults here instead of at each call site.
function getCanonicalWorkspacesParams(): Required<Pick<GetWorkspacesParams, "includeTerminated">> {
    return {
        includeTerminated: UserSettingsService.getIncludeTerminatedWorkspaces(),
    };
}

function useAllWorkspaces(params: GetWorkspacesParams = {}, options?: GetWorkspacesQueryOptions) {
    return useGetWorkspacesQuery({ ...getCanonicalWorkspacesParams(), ...params }, options);
}

function useCurrentWorkspace(params?: GetWorkspacesParams, options?: GetWorkspacesQueryOptions) {
    const isUserCredentialValid: boolean = useSelector(selectIsUserCredentialValid);

    const paramsCopy: GetWorkspacesParams = {
        // Always include settings so every useCurrentWorkspace caller shares the same cache key.
        // Splitting on includeSettings would produce two separate /workspaces requests on every page load.
        includeSettings: true,
        ...getCanonicalWorkspacesParams(),
        ...params,
    };

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    if (workspaceId !== null) {
        paramsCopy.workspaceId = workspaceId;
    }

    const optionsCopy = { ...options, skip: false };
    if (options && "skip" in options) {
        optionsCopy.skip = !!options.skip;
    }

    if (!paramsCopy.workspaceId) {
        optionsCopy.skip = true;
    }

    if (!isUserCredentialValid) {
        optionsCopy.skip = true;
    }

    const { data, ...rest } = useGetWorkspacesQuery(paramsCopy, optionsCopy);

    return { data: data?.[0] || null, ...rest };
}

function useSubscriptions(params: GetWorkspacesParams = {}, options?: GetWorkspacesQueryOptions) {
    const { data: workspaces, ...rest } = useAllWorkspaces(params, options);
    return { data: workspaces?.map((workspace) => workspace.subscription) || [], ...rest };
}

function useCurrentSubscription(params: GetWorkspacesParams) {
    const { data: workspace, ...rest } = useCurrentWorkspace(params);
    return { data: workspace?.subscription || null, ...rest };
}

function useCurrentSubscriptionExpired() {
    const { data: subscription } = useCurrentSubscription({});
    return Boolean(subscription?.isExpired);
}

type GetWorkspaceFeaturesQueryOptions = Omit<Parameters<typeof useGetWorkspaceFeaturesQuery>[1], "selectFromResult">;
function useCurrentWorkspaceFeatures(
    params?: Pick<GetWorkspaceFeaturesParams, "includeUsage">,
    options?: GetWorkspaceFeaturesQueryOptions,
) {
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const token = AuthService.getSession();

    const arg = !token || !workspaceId ? skipToken : { ...params, id: workspaceId };
    return useGetWorkspaceFeaturesQuery(arg, options);
}

function useIsWorkspaceFeatureLimitReached(featureType: keyof typeof FeatureType) {
    const { data: workspaceFeatures = [] } = useCurrentWorkspaceFeatures();
    const feature = workspaceFeatures.find((item) => item.feature === featureType);

    if (feature?.max === undefined) {
        return true;
    }
    return isUnlimited(feature.max, featureType) ? false : feature.max <= 0;
}

function useWorkspaceFeature(featureType: keyof typeof FeatureType) {
    const { data: workspaceFeatures = [] } = useCurrentWorkspaceFeatures();
    const feature = workspaceFeatures.find((item) => item.feature === featureType);
    return isUnlimited(feature?.max, featureType) ? Infinity : feature?.max || 0;
}

function useWorkspaceMemberRole(): WorkspaceMemberRole | null {
    const { data: workspace } = useCurrentWorkspace();
    if (!workspace) {
        return null;
    }

    const email = AuthService.getCurrentUser()?.email;
    if (!email) {
        return null;
    }

    const workspaceMember = workspace.members?.find((item) => item.email === email);
    if (!workspaceMember) {
        return null;
    }

    return workspaceMember.role;
}

function useSubscriptionExpiringWithinMonth() {
    const { data: subscription } = useCurrentSubscription({});

    if (!subscription?.expirationDate || subscription.cancellationDate) {
        return false;
    }

    const daysUntilExpiration = TimeService.getDaysDifferenceUntilNow(subscription.expirationDate);
    const thirtyDays = 30;

    if (daysUntilExpiration < 0) {
        return false;
    }

    return daysUntilExpiration <= thirtyDays;
}

export {
    useAllWorkspaces,
    useCurrentSubscription,
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useCurrentWorkspaceFeatures,
    useIsWorkspaceFeatureLimitReached,
    useSubscriptionExpiringWithinMonth,
    useSubscriptions,
    useWorkspaceFeature,
    useWorkspaceMemberRole,
};
